import {
  AfterViewChecked, Component, ElementRef, inject,
  ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {TranslateService} from '@ngx-translate/core';
import {merge} from 'rxjs';
import {lookupDisplayName} from '../lookup-controls';
import {AddSearchOption, AddSearchOptions, Assignment, ImportSessionStore, TargetField} from '../import-session.store';
import {conceptOptionLabel} from '../concept-picker/concept-option.model';
import {
  ComposeLinesSummary, composeLinesSummary, rowValueSourceKinds, SourcePiece, VALUE_SOURCE_KIND_LABELS,
  VALUE_SOURCE_KIND_TOOLTIPS, ValueSourceKind,
} from '../value-source';
import {BlankValueSuggestion} from '../blank-values-step/blank-values.model';
import {fieldTypeTagKey} from '../column-usages';
import {
  distinctText, showsColumn, SourceColumnEntry, SourceColumnFilter, sourceColumnEntries, SourceColumnTarget,
} from './source-column-list';
import {TargetSection} from './target-sections';

/** One option the add search shows: its pick, its name split around
 * the typed text, which shows in bold, and a field's type tag key (`null`
 * for a group kind). */
export interface AddSearchMatch {
  option: AddSearchOption;
  before: string;
  match: string;
  after: string;
  typeKey: string | null;
}

/**
 * The Import module's Feltkoblinger tab: the source columns, each with
 * where the mapping reads it, its column profile and its blank-value
 * suggestions; the group cards and the flat mapping rows under the
 * object form's section headings, with the add search over fields and
 * group kinds; and the batch settings that apply to every row. A row or
 * card shows its controls only while it is the store's opened line, and
 * a summary otherwise.
 */
@Component({
  selector: 'app-import-mapping-step',
  templateUrl: './import-mapping-step.component.html',
  styleUrls: ['./import-mapping-step.component.scss'],
  standalone: false,
})
export class ImportMappingStepComponent implements AfterViewChecked {
  readonly store: ImportSessionStore = inject(ImportSessionStore);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  readonly sourceKindLabels = VALUE_SOURCE_KIND_LABELS;
  readonly sourceKindTooltips = VALUE_SOURCE_KIND_TOOLTIPS;

  // The mapping row whose target box has focus or an open option list.
  // Only that row's autocomplete renders its options: Material creates
  // every projected option even while its panel is closed.
  targetOptionsRowId: number | null = null;

  // The add search. Page state only: typing in it or picking from it
  // changes no request body until a row or card is added.
  addSearchOpen = false;
  readonly addSearchControl = new FormControl<string | AddSearchOption | null>('');
  private addSearchMemo: {
    options: AddSearchOptions; query: string; translationVersion: number; matches: TargetSection<AddSearchMatch>[];
  } | null = null;
  // Moves when the translations a card option's name comes from do.
  private translationVersion = 0;
  @ViewChild('addSearchInput')
  private set addSearchInput(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref) {
      setTimeout(() => ref.nativeElement.focus());
    }
  }

  // The source column list's filters and its expanded column. Page state
  // only: none changes a request body, and none is kept in the session.
  columnFilter: SourceColumnFilter = 'all';
  readonly columnQueryControl = new FormControl<string>('', {nonNullable: true});
  expandedColumn: string | null = null;
  private columnEntryMemo: {
    columns: ImportSessionStore['columns'];
    profile: ImportSessionStore['columnProfile'];
    usages: ImportSessionStore['usagesByColumn'];
    used: ImportSessionStore['usedColumns'];
    joinColumn: string | null;
    suggestions: BlankValueSuggestion[];
    entries: SourceColumnEntry[];
  } | null = null;
  private shownColumnMemo: {
    entries: SourceColumnEntry[]; filter: SourceColumnFilter; query: string; shown: SourceColumnEntry[];
  } | null = null;

  // Each collapsed line's `composeLinesSummary`, by the source pieces it
  // splits, which the store keeps while the mapping stands.
  private readonly composeLinesMemo = new WeakMap<readonly SourcePiece[], ComposeLinesSummary | null>();

  // Set when a line is added from the add search or a column, so the
  // opened line is scrolled into view once it is on the page.
  private revealOpenedLine = false;
  @ViewChild('batchBlock') private batchBlock?: ElementRef<HTMLElement>;

  constructor() {
    merge(this.translate.onLangChange, this.translate.onTranslationChange, this.translate.onFallbackLangChange)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.translationVersion++);
  }

  ngAfterViewChecked(): void {
    if (this.revealOpenedLine) {
      this.revealOpenedLine = false;
      this.host.nativeElement.querySelector('.admin-import-module__opened-line')
        ?.scrollIntoView({block: 'nearest'});
    }
  }

  /** `pieces` as a collapsed Sammensatt line's rows
   * (`composeLinesSummary`): the same object while `pieces` is. */
  composeLinesOf(pieces: readonly SourcePiece[]): ComposeLinesSummary | null {
    let summary = this.composeLinesMemo.get(pieces);
    if (summary === undefined) {
      summary = composeLinesSummary(pieces);
      this.composeLinesMemo.set(pieces, summary);
    }
    return summary;
  }

  displayTargetField(value: TargetField | string | null): string {
    return value && typeof value === 'object' ? value.label : '';
  }

  onTargetFocus(assignment: Assignment): void {
    this.targetOptionsRowId = assignment.id;
  }

  /** The target box lost focus. With its option list open that is a click
   * on an option or outside the list, and `onTargetListClosed` follows;
   * dropping the options here would pull them from under the pointer. */
  onTargetBlur(assignment: Assignment, listOpen: boolean): void {
    if (!listOpen) {
      this.releaseTargetOptions(assignment);
    }
  }

  /** The target option list closed. It also closes on Escape, or when the
   * typed text matches nothing, with the box still focused; the options
   * stay for as long as it is. */
  onTargetListClosed(assignment: Assignment, input: HTMLInputElement): void {
    if (document.activeElement !== input) {
      this.releaseTargetOptions(assignment);
    }
  }

  private releaseTargetOptions(assignment: Assignment): void {
    if (this.targetOptionsRowId === assignment.id) {
      this.targetOptionsRowId = null;
    }
  }

  /** Whether `assignment`'s picked target field is a plain string/text
   * field - compose mode (the structured line repeater) is offered only
   * there, not for a concept/date/map_id target where composing several
   * optional prefixed columns wouldn't make sense: any
   * EditTextField/EditStringField target, not a fixed allow-list. */
  composeFieldAvailable(assignment: Assignment): boolean {
    return this.store.isFreeTextField(assignment.targetField);
  }

  /** The row's "Hentes fra" choices (`rowValueSourceKinds`), `kind` being
   * its current one: Kodeoppslag while there is a lookup, Sammensatt on a
   * free-text target. */
  sourceKinds(assignment: Assignment, kind: ValueSourceKind): readonly ValueSourceKind[] {
    return rowValueSourceKinds(kind, this.store.lookupDefinitions.length > 0, this.composeFieldAvailable(assignment));
  }

  openAddSearch(): void {
    this.addSearchOpen = true;
  }

  closeAddSearch(): void {
    this.addSearchOpen = false;
    this.addSearchControl.setValue('');
  }

  /** The group kinds and fields whose name holds the typed text, under
   * the store's section headings; a section with none is left out. The
   * same arrays while neither the text, the store's offer nor the
   * translations move. */
  get addSearchMatches(): TargetSection<AddSearchMatch>[] {
    const options = this.store.addSearchOptions;
    const value = this.addSearchControl.value;
    const query = (typeof value === 'string' ? value : '').toLowerCase().trim();
    const translationVersion = this.translationVersion;
    const memo = this.addSearchMemo;
    if (memo?.options !== options || memo.query !== query || memo.translationVersion !== translationVersion) {
      const matches: TargetSection<AddSearchMatch>[] = [];
      for (const section of options) {
        const lines = section.lines
          .map(option => addSearchMatch(option, this.addSearchLabel(option), query))
          .filter((match): match is AddSearchMatch => !!match);
        if (lines.length) {
          matches.push({...section, lines});
        }
      }
      this.addSearchMemo = {options, query, translationVersion, matches};
    }
    return this.addSearchMemo!.matches;
  }

  /** What an add-search option is tracked by: its group kind or its
   * field, which outlast the option objects a new offer builds. A field of
   * the linked media is kept apart from the same field on the object. */
  addSearchTrackKey(option: AddSearchOption): string {
    if (option.cfg) {
      return `card:${option.cfg.kind}`;
    }
    return option.media ? `media:${option.field.field_id}` : option.field.field_id;
  }

  /** A group kind's section title, or a field's name. */
  private addSearchLabel(option: AddSearchOption): string {
    return option.cfg ? this.translate.instant(option.cfg.sectionTitleKey) : option.field.label;
  }

  /** Adds the picked group kind's card or field's row, a field of the
   * linked media's among the rows onto those, which opens and is scrolled
   * into view. */
  onAddSearchPicked(event: MatAutocompleteSelectedEvent): void {
    const pick = event.option.value as AddSearchOption;
    this.closeAddSearch();
    if (pick.cfg) {
      this.store.addGroupOfKind(pick.cfg.kind);
    } else if (pick.media) {
      this.store.addMediaTargetField(pick.field);
    } else {
      this.store.addTargetField(pick.field);
    }
    this.revealOpenedLine = true;
  }

  /** Adds a row reading `column`, which opens and is scrolled into view. */
  addColumnRow(column: string): void {
    this.store.addAssignment(column);
    this.revealOpenedLine = true;
  }

  /** The source columns, built again when the columns, the column
   * profile, the mapping or the blank values move. */
  get columnEntries(): SourceColumnEntry[] {
    return this.columnEntryState().entries;
  }

  /** How many source columns the run reads. */
  get usedColumnCount(): number {
    return this.store.usedColumns.size;
  }

  /** The source columns the filters show. */
  get shownColumns(): SourceColumnEntry[] {
    const entries = this.columnEntries;
    const filter = this.columnFilter;
    const query = this.columnQueryControl.value;
    const memo = this.shownColumnMemo;
    if (memo?.entries !== entries || memo.filter !== filter || memo.query !== query) {
      this.shownColumnMemo = {
        entries, filter, query, shown: entries.filter(entry => showsColumn(filter, query, entry)),
      };
    }
    return this.shownColumnMemo!.shown;
  }

  private columnEntryState(): NonNullable<ImportMappingStepComponent['columnEntryMemo']> {
    const store = this.store;
    const memo = this.columnEntryMemo;
    const usages = store.usagesByColumn;
    const used = store.usedColumns;
    // The media join column's control does not move the mapping version.
    const joinColumn = store.mediaJoinColumn;
    const suggestions = store.blankValueSuggestions;
    if (memo?.columns === store.columns && memo.profile === store.columnProfile && memo.usages === usages
        && memo.used === used && memo.joinColumn === joinColumn && memo.suggestions === suggestions) {
      return memo;
    }
    const entries = sourceColumnEntries({
      columns: store.columns,
      profile: store.columnProfile?.columns ?? null,
      usages,
      used,
      mediaJoinColumn: joinColumn,
      suggestions,
      lookupName: key => {
        const lookup = store.lookupDefinitions.find(l => l.key === key);
        return lookup ? lookupDisplayName(lookup) : null;
      },
      isReadAsBlank: (column, value) => store.isReadAsBlank(column, value),
    });
    this.columnEntryMemo = {
      columns: store.columns, profile: store.columnProfile, usages, used, joinColumn, suggestions, entries,
    };
    return this.columnEntryMemo;
  }

  /** A place the run reads a column, and the lookup it reads it
   * through. */
  targetText(target: SourceColumnTarget): string {
    if (target.labelKey) {
      return this.translate.instant(target.labelKey);
    }
    return target.lookup
      ? this.translate.instant('TRANS__IMPORT__MAPPING__COLUMN_VIA_LOOKUP', {field: target.label, lookup: target.lookup})
      : target.label;
  }

  /** Opens `column`'s statistics and suggestions, or closes them. */
  toggleColumn(column: string): void {
    this.expandedColumn = this.expandedColumn === column ? null : column;
  }

  /** A column profile's distinct count, "200+" where it stopped. */
  readonly distinctText = distinctText;

  /** "står i N rader. Betyr det tomt?", in the singular for one row. */
  suggestionKey(count: number): string {
    return count === 1
      ? 'TRANS__IMPORT__MAPPING__COLUMN_SUGGESTION_ONE'
      : 'TRANS__IMPORT__MAPPING__COLUMN_SUGGESTION';
  }

  /** Scrolls "Gjelder alle radene" into view. */
  showBatchBlock(): void {
    this.batchBlock?.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  displayAddSearchPick(): string {
    return '';
  }

  /** The name of the root term a row is scoped to, while the row's root
   * terms are on the page; `null` before they are, or with no scope. */
  rootScopeName(assignment: Assignment): string | null {
    const root = assignment.conceptRootOptions.find(option => option.id === assignment.conceptRootControl.value);
    return root ? conceptOptionLabel(root) : null;
  }
}

/** `option` with `label` split around its first `query` (lower-cased),
 * or `null` where `label` does not hold it. An empty query matches
 * every label, with nothing in bold. */
function addSearchMatch(option: AddSearchOption, label: string, query: string): AddSearchMatch | null {
  const start = label.toLowerCase().indexOf(query);
  if (start < 0) {
    return null;
  }
  const end = start + query.length;
  return {
    option, before: label.slice(0, start), match: label.slice(start, end), after: label.slice(end),
    typeKey: option.field ? fieldTypeTagKey(option.field) : null,
  };
}
